import { z } from 'zod';
import { complianceDecisionSchema, complianceRuleSchema } from '../../requirements/compliance.js';
import { defineStepType } from '../contract.js';
import { hrRecordSubmissionSchema } from '../../credentials/evidence-details.js';
import { screeningRecurrenceSchema } from '../../requirements/screening-recurrence.js';
import { fileCategorySchema } from '../../files/categories.js';
/**
 * Every HR upload a record rests on, once each (#188): the record's own evidence and each
 * competency form's. Two assessments may cite one uploaded packet; it is committed once.
 */
export function hrRecordUploadIds(submission) {
    const ids = [submission.evidenceUploadId, ...(submission.competency ?? []).map((entry) => entry.evidenceUploadId)];
    return [...new Set(ids.filter((id) => id !== undefined))];
}
/**
 * HR records that something happened. The person submits nothing.
 *
 * Issue #85: eight blocking requirements had no path to satisfaction at all. Orientation is
 * the clearest — HR runs it in a room, the candidate has nothing to upload, and there was no
 * screen for HR to say it happened. A candidate could finish everything the portal asked and
 * stay "Not ready" forever.
 *
 * This is NOT a second configuration system. The requirements already exist and already
 * carry the satisfaction shapes; what was missing was a way to write evidence for them.
 *
 * `portal: null` on the UI half is the point (ADR-005): there is nothing for the candidate
 * to see, and `PortalStepRenderer` already renders that case as "recorded by our office".
 * Inventing a portal component that throws would be worse than saying so.
 */
function storeUploads(instanceId, category, uploadIds) {
    if (category === undefined)
        return [];
    return uploadIds.map((uploadId) => ({ kind: 'store-file', instanceId, uploadId, category, source: 'hr-upload', plan: null }));
}
export const hrRecord = defineStepType({
    type: 'hr-record',
    /** HR ran it in a room or received it directly — the person submits nothing, and the portal has no half for it (ADR-005 portal: null). */
    submittedBy: 'office',
    label: 'Recorded by our office',
    description: 'Something HR did or received directly — orientation, a background check result, a qualification review.',
    configSchema: z.object({
        heading: z.string().min(1),
        /** What HR is attesting to. Shown on the review card and in the audit export. */
        instructions: z.string().default(''),
        /**
         * Which evidence this produces, derived from the requirement's own `kind` when the
         * definition is created — never chosen by whoever clicks. A `credential` row needs a
         * CredentialItem to read as satisfied and an `external-check` row needs a
         * VerificationEvent; getting it wrong produces a record that looks right and satisfies
         * nothing.
         */
        produces: z.enum(['none', 'credential', 'verification']),
        requirementKey: z.string().min(1).nullable().default(null),
        /** True when the thing recorded carries an expiry the person must renew. */
        collectsExpiry: z.boolean().default(false),
        compliance: complianceRuleSchema.optional(),
        complianceDecision: complianceDecisionSchema.optional(),
        settingsRevision: z.number().int().nonnegative().optional(),
        screeningRecurrence: screeningRecurrenceSchema.extend({ settingsRevision: z.number().int().nonnegative().optional() }).optional(),
        /**
         * Where HR's uploaded evidence is committed (#188), from the requirement's own `fileCategory`
         * at the moment of recording — never chosen by whoever clicks. Segregation is derived from it,
         * so a health requirement's evidence lands in the restricted account. Absent on records made
         * before HR uploads existed, which carry no upload to store.
         */
        fileCategory: fileCategorySchema.optional(),
    }),
    submissionSchema: hrRecordSubmissionSchema,
    /**
     * One per competency, plus the record's own — the shape the structural read cannot see, and
     * the reason this accessor exists (#661). Reuses what `onApprove` already computes.
     */
    uploads: ({ submission }) => hrRecordUploadIds(submission),
    onApprove: ({ instance, config, submission }) => [
        // Committed through the same `store-file` effect as a candidate's upload: the scan gate and
        // the plan are applied at approval (`attachStorePlans`), the category decides the store. No
        // category means nowhere a file may go, so nothing is emitted and the approval is refused by
        // the caller rather than parking a file with no destination.
        ...storeUploads(instance.id, config.fileCategory, hrRecordUploadIds(submission)),
        ...(config.produces === 'credential' && config.requirementKey !== null
            ? [
                {
                    kind: 'mint-credential',
                    personId: instance.personId,
                    requirementKey: config.requirementKey,
                    identifier: submission.reference === '' ? null : submission.reference,
                    issuedAt: submission.occurredAt,
                    expiresAt: submission.expiresAt,
                    ...(submission.credentialDetails === undefined ? {} : { details: submission.credentialDetails }),
                    evidenceUrl: submission.evidenceUrl,
                },
            ]
            : []),
        ...(config.produces === 'verification' && config.requirementKey !== null
            ? [
                {
                    kind: 'record-verification',
                    personId: instance.personId,
                    requirementKey: config.requirementKey,
                    method: 'hr-recorded',
                    verifiedBy: submission.performedBy || submission.recordedBy,
                    recordedBy: submission.recordedBy,
                    occurredAt: submission.occurredAt,
                    outcome: submission.outcome,
                    source: submission.source,
                    reference: submission.reference,
                    note: submission.note,
                    evidenceUrl: submission.evidenceUrl,
                },
            ]
            : []),
        { kind: 'recompute-rollup', personId: instance.personId },
    ],
    expiresAt: ({ config, submission }) => (config.collectsExpiry ? submission.expiresAt : null),
});
//# sourceMappingURL=hr-record.js.map